import type { TelegramWebApp, TelegramThemeParams } from '@/types';

declare global {
  interface Window {
    Telegram?: {
      WebApp: TelegramWebApp;
    };
  }
}

/**
 * Telegram WebApp utilities
 */
export class TelegramUtils {
  private static _webApp: TelegramWebApp | null = null;

  /**
   * Get the Telegram WebApp instance
   */
  static get webApp(): TelegramWebApp | null {
    if (!this._webApp && typeof window !== 'undefined' && window.Telegram?.WebApp) {
      this._webApp = window.Telegram.WebApp;
    }
    return this._webApp;
  }

  /**
   * Check if Telegram is in light theme mode
   * Returns true ONLY if in Telegram AND theme is light
   * Returns false for browser (should use dark theme)
   */
  static get isLightTheme(): boolean {
    return this.isInTelegram && this.webApp?.colorScheme === 'light';
  }

  /**
   * Check if we're running inside Telegram WebApp
   */
  static get isInTelegram(): boolean {
    // More robust check - must have webApp, non-empty initData, and valid platform
    const tg = this.webApp;
    if (!tg || !tg.initData || tg.initData.trim() === '') return false;
    
    // Additional check - Telegram platform should be set
    const validPlatforms = ['android', 'ios', 'macos', 'tdesktop', 'weba', 'webk', 'web'];
    return validPlatforms.includes(tg.platform);
  }

  /**
   * Initialize Telegram WebApp
   */
  static init(): void {
    const tg = this.webApp;
    if (!tg) return;

    try {
      // Ready and expand the app
      tg.ready();
      tg.expand();

      // Disable vertical swipes to prevent app collapsing
      if (typeof (tg as any).isVerticalSwipesEnabled !== 'undefined') {
        (tg as any).isVerticalSwipesEnabled = false;
      }

      // Apply theme
      this.applyTheme();

      // Set up safe area CSS variables
      this.setupSafeArea();

      // Listen for theme changes
      tg.onEvent('themeChanged', this.applyTheme);

      // Listen for viewport changes
      tg.onEvent('viewportChanged', this.handleViewportChange);

      console.log('✅ Telegram WebApp initialized', {
        version: tg.version,
        platform: tg.platform,
        colorScheme: tg.colorScheme,
        isExpanded: tg.isExpanded,
        viewportHeight: tg.viewportHeight,
        isVerticalSwipesEnabled: (tg as any).isVerticalSwipesEnabled,
      });
    } catch (error) {
      console.error('❌ Failed to initialize Telegram WebApp:', error);
    }
  }

  /**
   * Apply Telegram theme to CSS variables
   */
  static applyTheme(): void {
    const tg = this.webApp;
    if (!tg) return;

    const theme = tg.themeParams;
    const root = document.documentElement;

    // Apply theme colors to CSS variables
    Object.entries(theme).forEach(([key, value]) => {
      if (value) {
        const cssVarName = `--tg-theme-${key.replace(/_/g, '-')}`;
        root.style.setProperty(cssVarName, value);
      }
    });

    // Update theme-color meta tag
    const themeColorMeta = document.querySelector('meta[name="theme-color"]');
    if (themeColorMeta && theme.bg_color) {
      themeColorMeta.setAttribute('content', theme.bg_color);
    }

    // Apply color scheme class ONLY if in real Telegram
    if (this.isInTelegram) {
      if (tg.colorScheme === 'light') {
        document.documentElement.classList.remove('dark');
        document.documentElement.classList.add('light');
      } else {
        document.documentElement.classList.remove('light');
        document.documentElement.classList.add('dark');
      }
    } else {
      // Browser mode - always use dark theme
      document.documentElement.classList.remove('light');
      document.documentElement.classList.add('dark');
    }
  }

  /**
   * Set up safe area CSS variables
   */
  static setupSafeArea(): void {
    const tg = this.webApp;
    if (!tg) return;

    const root = document.documentElement;
    
    // Get safe area insets from environment or use defaults
    const computedStyle = window.getComputedStyle(root);
    const safeAreaTop = computedStyle.getPropertyValue('env(safe-area-inset-top)') || '0px';
    const safeAreaRight = computedStyle.getPropertyValue('env(safe-area-inset-right)') || '0px';
    const safeAreaBottom = computedStyle.getPropertyValue('env(safe-area-inset-bottom)') || '0px';
    const safeAreaLeft = computedStyle.getPropertyValue('env(safe-area-inset-left)') || '0px';

    root.style.setProperty('--tg-safe-area-inset-top', safeAreaTop);
    root.style.setProperty('--tg-safe-area-inset-right', safeAreaRight);
    root.style.setProperty('--tg-safe-area-inset-bottom', safeAreaBottom);
    root.style.setProperty('--tg-safe-area-inset-left', safeAreaLeft);
  }

  /**
   * Handle viewport changes (keyboard show/hide)
   */
  static handleViewportChange(): void {
    const tg = this.webApp;
    if (!tg) return;

    // Update viewport height CSS variable
    document.documentElement.style.setProperty(
      '--tg-viewport-height',
      `${tg.viewportHeight}px`
    );

    // Dispatch custom event for React components
    window.dispatchEvent(new CustomEvent('telegram-viewport-changed', {
      detail: {
        height: tg.viewportHeight,
        stableHeight: tg.viewportStableHeight,
        isExpanded: tg.isExpanded,
      }
    }));
  }

  /**
   * Configure Back Button
   */
  static configureBackButton(
    visible: boolean,
    onClick?: () => void
  ): void {
    const tg = this.webApp;
    if (!tg) return;

    if (visible) {
      tg.BackButton.show();
      if (onClick) {
        tg.BackButton.onClick(onClick);
      }
    } else {
      tg.BackButton.hide();
    }
  }

  /**
   * Configure Main Button
   */
  static configureMainButton(config: {
    text?: string;
    color?: string;
    textColor?: string;
    visible?: boolean;
    active?: boolean;
    progress?: boolean;
    onClick?: () => void;
  }): void {
    const tg = this.webApp;
    if (!tg) return;

    const button = tg.MainButton;

    // Set button parameters
    if (config.text !== undefined) button.setText(config.text);
    
    // Set visibility
    if (config.visible !== undefined) {
      config.visible ? button.show() : button.hide();
    }

    // Set active state
    if (config.active !== undefined) {
      config.active ? button.enable() : button.disable();
    }

    // Set progress
    if (config.progress !== undefined) {
      config.progress ? button.showProgress() : button.hideProgress();
    }

    // Set click handler
    if (config.onClick) {
      button.onClick(config.onClick);
    }

    // Set colors using setParams for batch update
    const params: any = {};
    if (config.color) params.color = config.color;
    if (config.textColor) params.text_color = config.textColor;
    if (Object.keys(params).length > 0) {
      button.setParams(params);
    }
  }

  /**
   * Haptic feedback shortcuts
   */
  static haptic = {
    light: () => this.webApp?.HapticFeedback.impactOccurred('light'),
    medium: () => this.webApp?.HapticFeedback.impactOccurred('medium'),
    heavy: () => this.webApp?.HapticFeedback.impactOccurred('heavy'),
    success: () => this.webApp?.HapticFeedback.notificationOccurred('success'),
    error: () => this.webApp?.HapticFeedback.notificationOccurred('error'),
    warning: () => this.webApp?.HapticFeedback.notificationOccurred('warning'),
    selection: () => this.webApp?.HapticFeedback.selectionChanged(),
  };

  /**
   * Cloud Storage utilities
   */
  static storage = {
    set: (key: string, value: any): Promise<boolean> => {
      return new Promise((resolve, reject) => {
        const tg = this.webApp;
        if (!tg) {
          // Fallback to localStorage
          try {
            localStorage.setItem(`tg_${key}`, JSON.stringify(value));
            resolve(true);
          } catch (error) {
            reject(error);
          }
          return;
        }

        tg.CloudStorage.setItem(key, JSON.stringify(value), (error, stored) => {
          if (error) {
            // Fallback to localStorage
            try {
              localStorage.setItem(`tg_${key}`, JSON.stringify(value));
              resolve(true);
            } catch (fallbackError) {
              reject(fallbackError);
            }
          } else {
            resolve(stored);
          }
        });
      });
    },

    get: <T = any>(key: string): Promise<T | null> => {
      return new Promise((resolve, reject) => {
        const tg = this.webApp;
        if (!tg) {
          // Fallback to localStorage
          try {
            const item = localStorage.getItem(`tg_${key}`);
            resolve(item ? JSON.parse(item) : null);
          } catch (error) {
            reject(error);
          }
          return;
        }

        tg.CloudStorage.getItem(key, (error, value) => {
          if (error) {
            // Fallback to localStorage
            try {
              const item = localStorage.getItem(`tg_${key}`);
              resolve(item ? JSON.parse(item) : null);
            } catch (fallbackError) {
              reject(fallbackError);
            }
          } else {
            try {
              resolve(value ? JSON.parse(value) : null);
            } catch (parseError) {
              resolve(null);
            }
          }
        });
      });
    },

    remove: (key: string): Promise<boolean> => {
      return new Promise((resolve, reject) => {
        const tg = this.webApp;
        if (!tg) {
          // Fallback to localStorage
          try {
            localStorage.removeItem(`tg_${key}`);
            resolve(true);
          } catch (error) {
            reject(error);
          }
          return;
        }

        tg.CloudStorage.removeItem(key, (error, removed) => {
          if (error) {
            // Fallback to localStorage
            try {
              localStorage.removeItem(`tg_${key}`);
              resolve(true);
            } catch (fallbackError) {
              reject(fallbackError);
            }
          } else {
            resolve(removed);
          }
        });
      });
    },

    clear: (): Promise<boolean> => {
      return new Promise((resolve, reject) => {
        const tg = this.webApp;
        if (!tg) {
          // Fallback to localStorage
          try {
            Object.keys(localStorage)
              .filter(key => key.startsWith('tg_'))
              .forEach(key => localStorage.removeItem(key));
            resolve(true);
          } catch (error) {
            reject(error);
          }
          return;
        }

        tg.CloudStorage.getKeys((error, keys) => {
          if (error || !keys) {
            reject(error || new Error('Failed to get keys'));
            return;
          }

          if (keys.length === 0) {
            resolve(true);
            return;
          }

          tg.CloudStorage.removeItems(keys, (removeError, removed) => {
            if (removeError) {
              reject(removeError);
            } else {
              resolve(removed);
            }
          });
        });
      });
    },
  };

  /**
   * Show popup with Telegram native UI
   */
  static showPopup(
    message: string,
    title?: string,
    buttons?: Array<{ id: string; text: string; type?: 'default' | 'ok' | 'close' | 'cancel' | 'destructive' }>
  ): Promise<string | null> {
    return new Promise((resolve) => {
      const tg = this.webApp;
      
      // Check if we're actually in Telegram (has initData)
      if (!tg || !tg.initData || !tg.showPopup) {
        // Fallback to browser confirm
        if (confirm(`${title ? title + '\n\n' : ''}${message}`)) {
          resolve('ok');
        } else {
          resolve('cancel');
        }
        return;
      }

      try {
        tg.showPopup({
          title,
          message,
          buttons: buttons || [{ type: 'ok' }],
        }, (buttonId) => {
          resolve(buttonId || null);
        });
      } catch (error) {
        console.warn('Telegram showPopup failed, using browser confirm:', error);
        if (confirm(`${title ? title + '\n\n' : ''}${message}`)) {
          resolve('ok');
        } else {
          resolve('cancel');
        }
      }
    });
  }

  /**
   * Show alert with Telegram native UI
   */
  static showAlert(message: string): Promise<void> {
    return new Promise((resolve) => {
      const tg = this.webApp;
      
      // Check if we're actually in Telegram (has initData)
      if (!tg || !tg.initData || !tg.showAlert) {
        alert(message);
        resolve();
        return;
      }

      try {
        tg.showAlert(message, () => resolve());
      } catch (error) {
        console.warn('Telegram showAlert failed, using browser alert:', error);
        alert(message);
        resolve();
      }
    });
  }

  /**
   * Show confirm dialog with Telegram native UI
   */
  static showConfirm(message: string): Promise<boolean> {
    return new Promise((resolve) => {
      const tg = this.webApp;
      
      // Check if we're actually in Telegram (has initData)
      if (!tg || !tg.initData || !tg.showConfirm) {
        // Fallback to browser confirm
        resolve(confirm(message));
        return;
      }

      try {
        tg.showConfirm(message, (confirmed) => resolve(confirmed));
      } catch (error) {
        // If Telegram method fails, fallback to browser confirm
        console.warn('Telegram showConfirm failed, using browser confirm:', error);
        resolve(confirm(message));
      }
    });
  }

  /**
   * Open invoice for Telegram Stars payment
   */
  static openInvoice(invoiceLink: string): Promise<string> {
    return new Promise((resolve) => {
      const tg = this.webApp;
      
      if (!tg || !tg.initData) {
        // Not in Telegram - show message
        console.warn('Not in Telegram, payment not available');
        alert('Оплата доступна только в Telegram');
        resolve('failed');
        return;
      }

      try {
        // Call Telegram's openInvoice method
        if (typeof (tg as any).openInvoice === 'function') {
          (tg as any).openInvoice(invoiceLink, (status: string) => {
            // status = 'paid' | 'cancelled' | 'failed' | 'pending'
            console.log('Invoice callback status:', status);
            resolve(status);
          });
        } else {
          console.error('openInvoice method not available');
          alert('Метод оплаты недоступен в вашей версии Telegram');
          resolve('failed');
        }
      } catch (error) {
        console.error('Failed to open invoice:', error);
        alert('Ошибка при открытии платежа');
        resolve('failed');
      }
    });
  }

  /**
   * Close the WebApp
   */
  static close(): void {
    this.webApp?.close();
  }

  /**
   * Send data to the bot
   */
  static sendData(data: any): void {
    this.webApp?.sendData(JSON.stringify(data));
  }

  /**
   * Get user info from initDataUnsafe (for display only, never trust on client)
   */
  static getUserInfo() {
    return this.webApp?.initDataUnsafe.user || null;
  }

  /**
   * Get init data for server validation
   */
  static getInitData(): string {
    return this.webApp?.initData || '';
  }

  /**
   * Check if user has Telegram Premium
   */
  static get isPremiumUser(): boolean {
    return this.webApp?.initDataUnsafe.user?.is_premium || false;
  }

  /**
   * Get current theme params
   */
  static getThemeParams(): TelegramThemeParams {
    return this.webApp?.themeParams || {};
  }

  /**
   * Debug info for development
   */
  static getDebugInfo() {
    const tg = this.webApp;
    if (!tg) return null;

    return {
      version: tg.version,
      platform: tg.platform,
      colorScheme: tg.colorScheme,
      isExpanded: tg.isExpanded,
      viewportHeight: tg.viewportHeight,
      viewportStableHeight: tg.viewportStableHeight,
      headerHeight: tg.headerHeight,
      initData: tg.initData ? '[HIDDEN]' : null,
      user: tg.initDataUnsafe.user,
      themeParams: tg.themeParams,
    };
  }
}

// Auto-initialize when module loads
if (typeof window !== 'undefined') {
  // Wait for Telegram WebApp to be available
  const initTelegram = () => {
    if (window.Telegram?.WebApp) {
      TelegramUtils.init();
    } else {
      // Retry after a short delay
      setTimeout(initTelegram, 100);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initTelegram);
  } else {
    initTelegram();
  }
}
